import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiClock, FiAlertCircle, FiCheckCircle, FiInfo, FiTrendingUp, FiCalendar, FiDownload } from 'react-icons/fi';
import { Button } from '@/components/common/Button';
import api from '@/utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface ProgramUpdate {
  _id: string;
  title: string;
  content: string;
  type: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  image?: string;
  status: string;
  publishedAt: string;
  expiresAt?: string;
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
  views: number;
  createdAt: string;
}

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xs}`};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.xs}`};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};

  @media (max-width: 480px) {
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    font-size: ${({ theme }) => theme.fontSizes.sm};

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  flex: 1;
`;

const UpdateCount = styled.span`
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.xs}`};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.xs}`};
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.muted};
    }
  }
`;

const UpdatesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const UpdateCard = styled(motion.div)<{ $priority: string; $type: string; $read: boolean }>`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${({ $priority, theme }) => {
    switch ($priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return theme.colors.primary;
      case 'low': return '#10b981';
      default: return theme.colors.border;
    }
  }};
  border-top: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  border-right: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  border-bottom: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  opacity: ${({ $read }) => ($read ? 0.8 : 1)};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
  }
`;

const UpdateLayout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const UpdateImageWrapper = styled.div`
  flex: 0 0 120px;
  height: 80px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    height: 160px;
  }
`;

const UpdateTextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const UpdateHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const UpdateTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
  line-height: 1.4;
  flex: 1;
`;

const TypeBadge = styled.div<{ $type: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-transform: uppercase;
  background: ${({ $type }) => {
    switch ($type) {
      case 'alert': return '#ef444415';
      case 'milestone': return '#10b98115';
      case 'event': return '#8b5cf615';
      case 'news': return '#3b82f615';
      default: return '#64748b15';
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'alert': return '#ef4444';
      case 'milestone': return '#10b981';
      case 'event': return '#8b5cf6';
      case 'news': return '#3b82f6';
      default: return '#64748b';
    }
  }};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const UpdateContent = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.muted};
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const UpdateMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.muted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const AttachmentCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.primary};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.muted};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme}) => theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.muted};
  margin: 0;
  max-width: 400px;
`;

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'alert': return <FiAlertCircle />;
    case 'milestone': return <FiCheckCircle />;
    case 'event': return <FiCalendar />;
    case 'news': return <FiTrendingUp />;
    default: return <FiInfo />;
  }
};

export const ProgramUpdatesPage = () => {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState<ProgramUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [readMap, setReadMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        setLoading(true);
        const response = await api.get('/program-updates');
        const updatesData = response.data.data || [];
        setUpdates(updatesData);

        // Load read/acknowledged state from localStorage
        const stored = localStorage.getItem('programUpdatesRead');
        if (stored) {
          try {
            const ids: string[] = JSON.parse(stored);
            const map: Record<string, boolean> = {};
            ids.forEach((id) => {
              map[id] = true;
            });
            setReadMap(map);
          } catch {
            setReadMap({});
          }
        } else {
          setReadMap({});
        }
      } catch (error: any) {
        console.error('Error fetching program updates:', error);
        toast.error(error.response?.data?.message || 'Failed to load program updates');
      } finally {
        setLoading(false);
      }
    };

    fetchUpdates();
  }, []);

  const handleUpdateClick = async (update: ProgramUpdate) => {
    // Increment view count
    try {
      await api.patch(`/program-updates/${update._id}/view`);
    } catch (error) {
      console.error('Error incrementing views:', error);
    }

    // Show full update details (you can create a modal or detail page)
    toast.success('Opening details...');
  };

  const toggleRead = (id: string) => {
    setReadMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const readIds = Object.entries(next)
        .filter(([, value]) => value)
        .map(([id]) => id);
      localStorage.setItem('programUpdatesRead', JSON.stringify(readIds));
      return next;
    });
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>What is happening</Title>
        </Header>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Container>
    );
  }

  if (updates.length === 0) {
    return (
      <Container>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>Program Updates</Title>
        </Header>
        <EmptyState>
          <EmptyIcon>📢</EmptyIcon>
          <EmptyTitle>No updates available</EmptyTitle>
          <EmptyText>There are no program updates at the moment. Check back later!</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton variant="outline" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back
        </BackButton>
        <Title>What is happening</Title>
        <UpdateCount>{updates.length} {updates.length === 1 ? 'Update' : 'Updates'}</UpdateCount>
      </Header>

      <Content>
        <UpdatesGrid>
          {updates.map((update, index) => {
            const isRead = !!readMap[update._id];

            return (
              <UpdateCard
                key={update._id}
                $priority={update.priority}
                $type={update.type}
                $read={isRead}
                onClick={() => {
                  toggleRead(update._id);
                  handleUpdateClick(update);
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <UpdateLayout>
                  {update.image && (
                    <UpdateImageWrapper>
                      <img src={update.image} alt={update.title} />
                    </UpdateImageWrapper>
                  )}
                  <UpdateTextContent>
                    <UpdateHeader>
                      <UpdateTitle>{update.title}</UpdateTitle>
                      <TypeBadge $type={update.type}>
                        {getTypeIcon(update.type)}
                        {update.type}
                      </TypeBadge>
                    </UpdateHeader>

                    <UpdateContent>{update.content}</UpdateContent>

                    <UpdateMeta>
                      <MetaItem>
                        <FiClock />
                        {format(new Date(update.publishedAt || update.createdAt), 'MMM dd, yyyy')}
                      </MetaItem>
                      {update.attachments && update.attachments.length > 0 && (
                        <AttachmentCount>
                          <FiDownload />
                          {update.attachments.length} {update.attachments.length === 1 ? 'attachment' : 'attachments'}
                        </AttachmentCount>
                      )}
                    </UpdateMeta>
                  </UpdateTextContent>
                </UpdateLayout>
              </UpdateCard>
            );
          })}
        </UpdatesGrid>
      </Content>
    </Container>
  );
};
